import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import useFormattedMessage from './useFormattedMessage';
import {toast} from 'react-toastify';
import {classifyError, reportLoginError, isVerboseLoggingEnabled, newRef} from '../error/reportError';

function InnerDisplay({message, titleKey, messageKey, detail}) {
  const titleElement = titleKey ? <h3>{titleKey}</h3> : <h3>Error</h3>;
  const messageElement = messageKey ? <div>{messageKey}</div> : <div>{message}</div>;

  return (
    <div className="font-weight-bold small mb-0">
      <p className="mb-2">
        {titleElement}
        {messageElement}
        {detail ? (
          <div className="small mt-1" style={{color: '#fff'}}>
            {detail}
          </div>
        ) : null}
      </p>
    </div>
  );
}

InnerDisplay.propTypes = {
  message: PropTypes.string,
  titleKey: PropTypes.string,
  messageKey: PropTypes.string,
  detail: PropTypes.string,
};

// A unique reference code (matched to the beacon/event in our logs)
// plus which host/status/code actually failed.
function buildDetail(exception, info, refCode) {
  const failedUrl = exception.config && exception.config.url;
  let host = '';
  try {
    host = failedUrl ? new URL(failedUrl, window.location.origin).host : '';
  } catch (e) {
    host = '';
  }
  const parts = [host, info.status, info.code].filter(Boolean).join(' · ');
  const suffix = parts ? ` (${parts})` : '';
  if (refCode) {
    return `Ref: ${refCode}${suffix}`;
  }
  return parts ? `(${parts})` : undefined;
}

export function useErrorDisplay(exception, context) {
  const {formatMessage} = useFormattedMessage();

  useEffect(() => {
    if (!exception) return;

    // One reference code per failure, goes through both the beacon and the toast.
    // Only when verbose logging is on -- otherwise behaviour is unchanged.
    const verbose = isVerboseLoggingEnabled();
    const ref = verbose ? newRef() : null;

    reportLoginError(exception, {...(context || {}), ref});

    const info = classifyError(exception);
    const detail = verbose ? buildDetail(exception, info, ref) : undefined;
    const failedUrl = exception.config && exception.config.url;

    if (info.category === 'timeout') {
      // Request was aborted by our own timeout -> the server never answered.
      console.log('Login request timed out', failedUrl, exception);
      toast.error(
        <InnerDisplay
          titleKey={formatMessage('error.network.title')}
          messageKey={formatMessage('error.network')}
          detail={detail}
        />,
      );
    } else if (info.category === 'http_error') {
      // Request made and server responded with an error status.
      console.log('Server error', info.status, failedUrl, exception.response && exception.response.data);
      toast.error(
        <InnerDisplay
          titleKey={formatMessage('error.server_error.title')}
          messageKey={formatMessage('error.server_error')}
          detail={detail}
        />,
      );
    } else if (info.category === 'network_or_cors') {
      // Request made but no response received (network down, CORS, DNS, server down).
      console.log('No response (network/CORS/server down)', failedUrl, exception.request);
      toast.error(
        <InnerDisplay
          titleKey={formatMessage('error.network.title')}
          messageKey={formatMessage('error.network')}
          detail={detail}
        />,
      );
    } else {
      // Something threw while setting up the request. Should not happen.
      console.log('Error when setting up request', exception.message);
      toast.error(<InnerDisplay message={`Internal error: ${exception.message}`} detail={detail} />);
    }
  }, [exception]);
}

useErrorDisplay.propTypes = {
  exception: PropTypes.object.isRequired,
};
